import { LocalitiesTable } from './LocalitiesTable'
import { useTranslation } from 'react-i18next'
import { endpoints } from '../../../api/localities'
import { useCustomerCtx } from '../../../hooks/context-hooks'
import { Box, useTheme } from '@mui/material'
import { FC, useCallback } from 'react'
import LocalityDetailPanel from './LocalityDetailPanel'
import { ILocality } from '../../../generated-types'
import { UpdateLocalityDialog } from '../../localities/UpdateLocalityDialog'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { CreateLocalitiesDialog } from './CreateLocalityDialog'
import { ResourceScreenProps } from '../../../generated-types/resource-screen'
import { localityQueryKeys } from '../../../query-keys'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { AddCircleOutline } from '@mui/icons-material'

export const LocalitiesScreen: FC<ResourceScreenProps> = ({
  renderTableExpand,
  query,
  params,
  generateQueryKey,
  handleAdd,
  handleUpdate,
  isActionAvailable = true,
  isTableExpandable = true,
}) => {
  const { customer } = useCustomerCtx()
  const { t: translate } = useTranslation(['admin-locality', 'common'])
  const { showDialog } = useDialogCtx()
  const theme = useTheme()

  const onAdd = async () => {
    const added = await showDialog(
      CreateLocalitiesDialog,
      {
        componentProps: {
          customerId: customer.id as EntityId,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }

  const onUpdate = useCallback(
    async locality => {
      await showDialog(
        UpdateLocalityDialog,
        {
          title: translate('updateLocality', { ns: 'customer-locality' }),
          componentProps: { customerId: customer.id, locality: locality, t: translate },
        },
        undefined,
        true,
      )
    },
    [customer.id, translate],
  )

  return (
    <CommonPageLayout
      titleSection={{ title: translate('localities') }}
      topAction={[
        {
          buttonText: translate('locality', { ns: 'page-container' }),
          buttonIcon: <AddCircleOutline />,
          onClick: handleAdd ?? onAdd,
          variant: 'outlined',
          shadowWeight: null,
          border: 0,
        },
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <LocalitiesTable
          getDetailPanelContent={
            isTableExpandable
              ? renderTableExpand ?? (params => <LocalityDetailPanel locality={params.row as ILocality} />)
              : undefined
          }
          queryUrl={query ?? endpoints.getLocalitiesWithMinimalInfo(customer.id)}
          queryParams={params ? { ...params, isIgnoreQueryFilters: true } : { isIgnoreQueryFilters: true }}
          generateQueryKey={
            generateQueryKey ??
            ((page: number, pageSize: number) =>
              localityQueryKeys.GET_LOCALITIES(customer.id, { page, pageSize, ...params }))
          }
          onUpdate={isActionAvailable ? handleUpdate ?? onUpdate : undefined}
          customerId={customer.id}
        />
      </Box>
    </CommonPageLayout>
  )
}
